import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField, createFilterOptions } from "@mui/material";
import { searchBrokerInstruments } from "../../api/watchlist";
import modalStyles from "../MultiAccountOrderModal/MultiAccountOrderModal.module.css";
import {
  INSTRUMENT_SEARCH_DEBOUNCE_MS,
  mergeInstrumentSymbolOptions,
  tokenizeInstrumentSearchInput,
} from "./instrumentSearchUtils";
import {
  mapInstrumentSearchResults,
  MIN_INSTRUMENT_SEARCH_LENGTH,
} from "../Watchlist/instrumentSearchData";

function InstrumentTradingSymbolField({
  nearSymbol,
  value,
  exchange,
  disabled = false,
  onChange,
}) {
  const selectedSymbol = value || nearSymbol;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const instrumentCacheRef = useRef({});
  const selectedSymbolRef = useRef(selectedSymbol);

  useEffect(() => {
    selectedSymbolRef.current = selectedSymbol;
  }, [selectedSymbol]);

  const normalizedExchange = useMemo(
    () => String(exchange || "NSE").trim().toLowerCase(),
    [exchange]
  );

  const fetchOptions = useCallback(
    async (queryText, currentSymbol = "") => {
      const trimmed = String(queryText || "").trim();
      if (trimmed.length < MIN_INSTRUMENT_SEARCH_LENGTH) {
        requestIdRef.current += 1;
        setLoading(false);
        setOptions([]);
        return;
      }

      const tokens = tokenizeInstrumentSearchInput(queryText);
      if (tokens.length === 0) {
        setOptions(mergeInstrumentSymbolOptions([], currentSymbol));
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const response = await searchBrokerInstruments({
          q: tokens,
          exchange: normalizedExchange,
        });

        if (requestId !== requestIdRef.current) return;

        const instruments = mapInstrumentSearchResults(response);
        instruments.forEach((instrument) => {
          if (instrument.symbol) {
            instrumentCacheRef.current[instrument.symbol] = instrument;
          }
        });

        const symbols = instruments.map((item) => item.symbol).filter(Boolean);
        setOptions(mergeInstrumentSymbolOptions(symbols, currentSymbol));
      } catch {
        if (requestId !== requestIdRef.current) return;
        setOptions(mergeInstrumentSymbolOptions([], currentSymbol));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [normalizedExchange]
  );

  const scheduleFetch = useCallback(
    (queryText) => {
      const trimmed = String(queryText || "").trim();
      if (trimmed.length < MIN_INSTRUMENT_SEARCH_LENGTH) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        requestIdRef.current += 1;
        setLoading(false);
        setOptions([]);
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        fetchOptions(queryText, selectedSymbolRef.current);
      }, INSTRUMENT_SEARCH_DEBOUNCE_MS);
    },
    [fetchOptions]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      requestIdRef.current += 1;
    };
  }, []);

  const defaultFilterOptions = useMemo(() => createFilterOptions(), []);

  const handleFilterOptions = useCallback(
    (opts, state) => {
      if (state.inputValue.trim().length < MIN_INSTRUMENT_SEARCH_LENGTH) {
        return [];
      }
      return defaultFilterOptions(opts, state);
    },
    [defaultFilterOptions]
  );

  const handleOpen = useCallback(() => {
    setDropdownOpen(true);
    setSearchInput("");
    setOptions([]);
    requestIdRef.current += 1;
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    setDropdownOpen(false);
    setSearchInput("");
    setOptions([]);
    requestIdRef.current += 1;
    setLoading(false);
  }, []);

  const noOptionsText = useMemo(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length < MIN_INSTRUMENT_SEARCH_LENGTH) {
      return "Type at least 3 characters to search";
    }
    return "No symbols found";
  }, [searchInput]);

  const listStatusText = useCallback(
    (text) => <span className={modalStyles["modal-reprice-select-status"]}>{text}</span>,
    []
  );

  return (
    <Autocomplete
      size="small"
      disableClearable
      disabled={disabled}
      open={dropdownOpen}
      options={options}
      value={selectedSymbol}
      inputValue={dropdownOpen ? searchInput : selectedSymbol}
      loading={loading}
      loadingText={listStatusText("Searching…")}
      noOptionsText={listStatusText(noOptionsText)}
      filterOptions={handleFilterOptions}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={(_, newValue) => {
        if (!newValue) return;
        const instrument = instrumentCacheRef.current[newValue];
        onChange?.(
          newValue,
          instrument
            ? {
                exchange: instrument.exchange,
                exchange_token: instrument.exchange_token,
              }
            : undefined
        );
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "reset") return;
        setSearchInput(newInputValue);
        scheduleFetch(newInputValue);
      }}
      isOptionEqualToValue={(option, optionValue) => option === optionValue}
      getOptionLabel={(option) => option}
      className={modalStyles["modal-reprice-select"]}
      slotProps={{
        paper: {
          className: modalStyles["modal-reprice-select-paper"],
        },
        listbox: {
          className: modalStyles["modal-reprice-select-listbox"],
        },
        popupIndicator: {
          sx: loading ? { display: "none" } : undefined,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            disableUnderline: false,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    size={12}
                    thickness={5}
                    className={modalStyles["modal-reprice-select-spinner"]}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default InstrumentTradingSymbolField;
